import { parseUrlToIndexUiState } from '../algolia/url-handling';

/**
 * Lightweight check for "is this pathname an SRP (search results page)".
 * Kept in this module (not a separate import) so lazy chunks (e.g. MobileMenuModal) do not
 * trigger an extra module load that can fail in Vite dev with "error loading dynamically imported module".
 * Re-exported from `is-srp-route.ts` for tests and direct callers.
 */
export function isSRPRoute(pathname: string): boolean {
  const jobDetailPattern = /^\/(en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/([^/]+)/;
  if (jobDetailPattern.test(pathname)) {
    return false;
  }

  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  const isLocale = first && ['en', 'fi', 'sv'].includes(first);

  if (segments.length === 0 || (segments.length === 1 && isLocale)) {
    return true;
  }

  const jobsListingSegments = ['jobs', 'tyopaikat', 'lediga-jobb', 'jobb'];
  const jobsIndex = isLocale
    ? segments.findIndex((s, idx) => idx > 0 && jobsListingSegments.includes(s))
    : segments.findIndex((s) => jobsListingSegments.includes(s));

  if (jobsIndex !== -1) {
    return true;
  }

  return false;
}

export type UserMenuSrpJobsRow =
  | { action: 'hide' }
  | { action: 'find_jobs'; href: string }
  | { action: 'reset_search'; href: string };

function splitPathnameAndSearch(input: string): { pathname: string; search: string } {
  if (input.includes('://')) {
    try {
      const u = new URL(input);
      return { pathname: u.pathname, search: u.search.slice(1) };
    } catch {
      /* fall through */
    }
  }
  const q = input.indexOf('?');
  if (q === -1) {
    return { pathname: input.split('#')[0] || '/', search: '' };
  }
  return {
    pathname: input.slice(0, q).split('#')[0] || '/',
    search: input.slice(q + 1),
  };
}

/**
 * Whether the SRP pathname has a location (or equivalent) segment in the path,
 * matching +route.ts shapes: /{locale}/jobs/location/value, /jobs/location/value, etc.
 */
export function srpPathHasLocationFilter(pathname: string): boolean {
  const path = pathname.split('?')[0].replace(/\/$/, '') || '/';

  const withLocale = path.match(
    /^\/(en|sv|fi)(?:\/(jobs|lediga-jobb|jobb|tyopaikat)(?:\/(location|omrade|omr%C3%A5de|område|alue)(?:\/([^/]+))?)?)?$/
  );
  if (withLocale) {
    const value = withLocale[4];
    return Boolean(value && String(value).length > 0);
  }

  const noLocale = path.match(
    /^\/(jobs|lediga-jobb|jobb|tyopaikat)(?:\/(location|omrade|omr%C3%A5de|område|alue)(?:\/([^/]+))?)?$/
  );
  if (noLocale) {
    const value = noLocale[3];
    return Boolean(value && String(value).length > 0);
  }

  if (path === '/' || /^\/(en|fi|sv)$/.test(path)) {
    return false;
  }

  return /\/(location|alue|omrade|område|omr%C3%A5de)\/[^/]+/i.test(path);
}

function indexUiStateHasActiveSearch(state: ReturnType<typeof parseUrlToIndexUiState>): boolean {
  if (state.query && state.query.trim().length > 0) {
    return true;
  }
  if (state.page !== undefined && state.page !== null && state.page > 0) {
    return true;
  }
  if (state.sort && state.sort !== 'relevance') {
    return true;
  }
  if (state.refinementList && Object.keys(state.refinementList).length > 0) {
    return true;
  }
  if (state.isFinland === false) {
    return true;
  }
  return false;
}

/**
 * Controls the first row in the user menu: hide on SRP home, "Reset search" when the SRP has
 * path or URL filters, otherwise "Find Jobs" to the locale SRP home.
 */
export function getUserMenuSrpJobsRow(locale: string, pathnameOrFullUrl: string): UserMenuSrpJobsRow {
  const normalizedLocale = ['en', 'fi', 'sv'].includes(locale) ? locale : 'en';
  const href = normalizedLocale === 'en' ? '/' : `/${normalizedLocale}`;
  const { pathname, search } = splitPathnameAndSearch(pathnameOrFullUrl);

  if (!isSRPRoute(pathname)) {
    return { action: 'find_jobs', href };
  }

  const pathFiltered = srpPathHasLocationFilter(pathname);
  const pathWithQuery = search ? `${pathname}?${search}` : pathname;
  const indexState = parseUrlToIndexUiState(pathWithQuery);
  const queryFiltered = indexUiStateHasActiveSearch(indexState);

  if (!pathFiltered && !queryFiltered) {
    return { action: 'hide' };
  }

  return { action: 'reset_search', href };
}
